import axios from "axios";

// API Configuration - Use environment variables for security
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

// Centralized API configuration
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  ENDPOINTS: {
    AI_GENERATE: "/api/ai/generate",
    AI_EDIT: "/api/ai/edit",
    AI_HISTORY: "/api/ai/history",
    AI_EVENTS: "/api/ai/events",
    AUTH_LOGIN: "/api/auth/login",
    AUTH_SIGNUP: "/api/auth/signup",
    AUTH_VERIFY: "/api/auth/verify",
    AUTH_BIND_MOBILE: "/api/auth/bind-mobile",
    DESIGNS: "/api/designs",
    ADMIN_AUTH_LOGIN: "/api/admin/auth/login",
    ADMIN_STATS: "/api/admin/stats/overview",
    ADMIN_ORDERS: "/api/admin/orders",
    ADMIN_USERS: "/api/admin/users",
    ADMIN_USER_AI_EVENTS: "/api/admin/users/:id/ai-events",
    ADMIN_CONTEST_SETTINGS: "/api/admin/contest/settings",
    ADMIN_SETTINGS: "/api/admin/settings",
    CONTEST_SETTINGS: "/api/contest/settings",
    // Admin customization endpoints
    ADMIN_CUSTOMIZATIONS_ABAYA_TYPES: "/api/admin/customizations/abaya-types",
    ADMIN_CUSTOMIZATIONS_COLORS: "/api/admin/customizations/colors",
    ADMIN_CUSTOMIZATIONS_FABRICS: "/api/admin/customizations/fabrics",
    ADMIN_CUSTOMIZATIONS_EMBROIDERY:
      "/api/admin/customizations/embroidery-locations",
    ADMIN_CUSTOMIZATIONS_REFERENCE_IMAGES:
      "/api/admin/customizations/reference-images",
    ADMIN_CUSTOMIZATIONS_TRIMMINGS:
      "/api/admin/customizations/trimmings-items",
    ADMIN_CUSTOMIZATIONS_TRIMMINGS_EXPLAINER_IMAGE:
      "/api/admin/customizations/trimmings-items/explainer-image",
    ADMIN_CUSTOMIZATIONS_SLEEVES: "/api/admin/customizations/sleeves",
    ADMIN_CUSTOMIZATIONS_COLLARS: "/api/admin/customizations/collars",
    ADMIN_CUSTOMIZATIONS_ORDER_SIZES: "/api/admin/customizations/order-sizes",
    ADMIN_CUSTOMIZATIONS_ORDER_CLOSINGS:
      "/api/admin/customizations/order-closings",
    ADMIN_CUSTOMIZATIONS_ORDER_SCARVES:
      "/api/admin/customizations/order-scarves",
    ADMIN_CUSTOMIZATIONS_ORDER_UNDERLAYERS:
      "/api/admin/customizations/order-underlayers",
    ADMIN_CUSTOMIZATIONS_ORDER_SHOULDERS:
      "/api/admin/customizations/order-shoulders",
    ADMIN_CUSTOMIZATIONS_ORDER_DRESSES:
      "/api/admin/customizations/order-dresses",
    ADMIN_CUSTOMIZATIONS_AVAILABILITY_MATRIX:
      "/api/admin/customizations/availability-matrix",
    // Customization endpoints
    CUSTOMIZATIONS_ABAYA_TYPES: "/api/customizations/abaya-types",
    CUSTOMIZATIONS_COLORS: "/api/customizations/colors",
    CUSTOMIZATIONS_FABRICS: "/api/customizations/fabrics",
    CUSTOMIZATIONS_EMBROIDERY_LOCATIONS:
      "/api/customizations/embroidery-locations",
    CUSTOMIZATIONS_REFERENCE_IMAGES: "/api/customizations/reference-images",
    CUSTOMIZATIONS_TRIMMINGS_ITEMS: "/api/customizations/trimmings-items",
    CUSTOMIZATIONS_TRIMMINGS_EXPLAINER_IMAGE:
      "/api/customizations/trimmings-explainer-image",
    CUSTOMIZATIONS_SLEEVES: "/api/customizations/sleeves",
    CUSTOMIZATIONS_COLLARS: "/api/customizations/collars",
    CUSTOMIZATIONS_ORDER_OPTIONS: "/api/customizations/order-options",
    CUSTOMIZATIONS_ALL: "/api/customizations/all",
    UPLOAD_REFERENCE_IMAGE: "/api/storage/upload-reference-image",
  },
} as const;

// Helper function to build full API URLs
export const getApiUrl = (
  endpoint: keyof typeof API_CONFIG.ENDPOINTS
): string => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS[endpoint]}`;
};

// Legacy endpoints for backward compatibility
export const API_ENDPOINTS = {
  AI_GENERATE: getApiUrl("AI_GENERATE"),
  AI_EDIT: getApiUrl("AI_EDIT"),
  AI_HISTORY: getApiUrl("AI_HISTORY"),
  AI_EVENTS: getApiUrl("AI_EVENTS"),
  ADMIN_USER_AI_EVENTS: getApiUrl("ADMIN_USER_AI_EVENTS"),
  AUTH_LOGIN: getApiUrl("AUTH_LOGIN"),
  AUTH_SIGNUP: getApiUrl("AUTH_SIGNUP"),
  DESIGNS: getApiUrl("DESIGNS"),
} as const;

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to landing page
      localStorage.removeItem("authToken");
      // Dispatch custom event to notify AuthContext
      window.dispatchEvent(new Event("auth-logout"));
      // Don't redirect if we're already on the landing page or on an admin page
      if (
        window.location.pathname !== "/" &&
        !window.location.pathname.startsWith("/admin")
      ) {
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);
